import makeLogger from "../shared/lib/log";
import debounce from "./debounce";

const logger = makeLogger("ai.roas.interaction");

export enum Event {
  Click,
  DoubleClick,
  MouseDown,
  MouseUp,
  MouseMove,
  KeyDown,
  KeyUp,
  KeyPress,
  TouchStart,
  TouchEnd,
  TouchMove,
  Scroll,
  Resize,
  Focus,
  Blur,
  Input,
  Change,
  Submit,
  Unknown = -1,
}

const TRACKED_EVENTS = [
  "click",
  //"dblclick",
  //"mousedown",
  //"mouseup",
  //"mousemove",
  //"keydown",
  //"keyup",
  //"keypress",
  "touchstart",
  "touchend",
  "touchmove",
  "scroll",
  "resize",
  "focus",
  "blur",
  //"input",
  "change",
  "submit",
];

const DEBOUNCED_EVENTS = ["scroll", "resize"];

export interface Interaction {
  event: Event;
  delta: number;
  timestamp: number;
  target: {
    id?: string;
    name?: string;
    tagName: string;
    className?: string;
    innerText?: string;
  };
  data: Record<string, any>;
}

export class InteractionRecorder {
  private t0: number = new Date().getTime();
  private interactions: Interaction[] = [];

  async flush(): Promise<Interaction[]> {
    const interactions = [...this.interactions];
    this.interactions = [];
    return interactions;
  }

  async record() {
    TRACKED_EVENTS.forEach((event) => {
      let listener = (e: any) => {
        // If the event happens to target an element whose parent is
        // more interesting (i.e. off of which we're more likely to
        // gather better data, then target that element instead).
        let target = e.target as HTMLElement;
        if (!isInteresting(target) && isInteresting(target.parentElement)) {
          target = target.parentElement;
        }

        // Taps and clicks on some elements will contain entirely too
        // much text, so filter those out. If a piece of text is longer
        // than 100 bytes, trim it. Also, encode it to avoid issues
        // with split codepoints. If a codepoint is split, the call to
        // encodeURIComponent will fail.
        let text = "";
        if (!isStructural(target)) {
          text = target?.innerText || "";
          if (text.length > 100) {
            text = `${text.substring(0, 100)}`;
            text = encodeURIComponent(text);
          }
        }

        const timestamp = new Date().getTime();
        const interaction = {
          event: getType(e.type),
          delta: timestamp - this.t0,
          timestamp: timestamp,
          target: {
            id: target?.id,
            name: (target as any)?.name,
            tagName: target?.tagName,
            className: target?.className,
            innerText: text,
          },
          data: getData(e),
        };
        logger.debug("interaction", interaction);
        this.interactions.push(interaction);
      };
      if (DEBOUNCED_EVENTS.indexOf(event) !== -1) {
        listener = debounce(50, listener);
      }

      document.addEventListener(event, listener, true);
    });
  }
}

const getType = (typ: string | undefined): Event => {
  switch (typ) {
    case "click":
      return Event.Click;
    case "dblclick":
      return Event.DoubleClick;
    case "mousedown":
      return Event.MouseDown;
    case "mouseup":
      return Event.MouseUp;
    case "mousemove":
      return Event.MouseMove;
    case "keydown":
      return Event.KeyDown;
    case "keyup":
      return Event.KeyUp;
    case "keypress":
      return Event.KeyPress;
    case "touchstart":
      return Event.TouchStart;
    case "touchend":
      return Event.TouchEnd;
    case "touchmove":
      return Event.TouchMove;
    case "scroll":
      return Event.Scroll;
    case "resize":
      return Event.Resize;
    case "focus":
      return Event.Focus;
    case "blur":
      return Event.Blur;
    case "input":
      return Event.Input;
    case "change":
      return Event.Change;
    case "submit":
      return Event.Submit;
    default:
      return Event.Unknown;
  }
};

const getData = (e: any): Record<string, any> => {
  switch (e.type) {
    case "click":
    case "mousedown":
    case "mouseup":
      return { x: e.clientX, y: e.clientY, button: e.button };
    case "keydown":
    case "keyup":
    case "keypress":
      return {
        key: e.key,
        code: e.code,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
      };
    case "input":
    case "change":
      return { value: e.target.value };
    case "scroll":
      return { scrollPct: window.scrollY / document.body.scrollHeight };
    case "touchstart":
    case "touchend":
    case "touchmove":
      return {
        touches: Array.from(e.touches).map((touch: Touch) => ({
          x: touch.clientX,
          y: touch.clientY,
        })),
      };
    default:
      return {};
  }
};

const isInteresting = (element: HTMLElement | undefined): boolean => {
  switch (element?.tagName) {
    case "A":
    case "BUTTON":
      return true;
    default:
      return false;
  }
};

const isStructural = (element: HTMLElement | undefined): boolean => {
  switch (element?.tagName) {
    case "ARTICLE":
    case "BODY":
    case "CANVAS":
    case "DIV":
    case "FIELDSET":
    case "FIGURE":
    case "FOOTER":
    case "FORM":
    case "HEADER":
    case "HR":
    case "MAIN":
    case "NAV":
    case "NOSCRIPT":
    case "OL":
    case "PRE":
    case "SECTION":
    case "TABLE":
    case "TFOOT":
    case "UL":
    case "VIDEO":
      return true;
    default:
      return false;
  }
};
